import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, ArrowLeft, Crown, UserPlus, Mail, Trash2, 
  Check, X, LogOut, MoreVertical, FolderOpen, Shield, Bell, Send, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TEAM_MEMBERS = 12;

const Teams = ({ user }) => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteTeamDialog, setShowDeleteTeamDialog] = useState(false);
  const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(null);
  const [showLeaveTeamDialog, setShowLeaveTeamDialog] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  
  // Form states
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifying, setNotifying] = useState(false);

  useEffect(() => {
    fetchTeams();
    fetchPendingInvitations();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const response = await axios.get(`${API}/teams/invitations/pending`, { withCredentials: true });
      setPendingInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const fetchTeamDetails = async (teamId) => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}`, { withCredentials: true });
      setSelectedTeam(response.data);
    } catch (error) {
      console.error('Error fetching team details:', error);
      toast.error('Failed to load team details');
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/teams`, newTeam, { withCredentials: true });
      toast.success('Team created!');
      setShowCreateDialog(false);
      setNewTeam({ name: '', description: '' });
      fetchTeams();
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim() || !selectedTeam) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/teams/${selectedTeam.team_id}/invite`, {
        email: inviteEmail
      }, { withCredentials: true });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteDialog(false);
      setInviteEmail('');
      fetchTeamDetails(selectedTeam.team_id);
    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptInvitation = async (inviteId) => {
    try {
      await axios.post(`${API}/teams/invitations/${inviteId}/accept`, {}, { withCredentials: true });
      toast.success('You have joined the team!');
      fetchTeams();
      fetchPendingInvitations();
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (inviteId) => {
    try {
      await axios.post(`${API}/teams/invitations/${inviteId}/decline`, {}, { withCredentials: true });
      toast.success('Invitation declined');
      fetchPendingInvitations();
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast.error('Failed to decline invitation');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!selectedTeam) return;
    
    try {
      await axios.delete(`${API}/teams/${selectedTeam.team_id}/members/${memberId}`, { withCredentials: true });
      toast.success('Member removed');
      setShowRemoveMemberDialog(null);
      fetchTeamDetails(selectedTeam.team_id);
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  const handleLeaveTeam = async () => {
    if (!selectedTeam) return;
    
    try {
      await axios.delete(`${API}/teams/${selectedTeam.team_id}/members/${user.user_id}`, { withCredentials: true });
      toast.success('You have left the team');
      setShowLeaveTeamDialog(false);
      setSelectedTeam(null);
      fetchTeams();
    } catch (error) {
      console.error('Error leaving team:', error);
      toast.error('Failed to leave team');
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    
    try {
      await axios.delete(`${API}/teams/${selectedTeam.team_id}`, { withCredentials: true });
      toast.success('Team deleted');
      setShowDeleteTeamDialog(false);
      setSelectedTeam(null);
      fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    }
  };

  const handleNotifyTeam = async () => {
    if (!selectedTeam) return;
    
    setNotifying(true);
    try {
      const response = await axios.post(`${API}/teams/${selectedTeam.team_id}/notify-due-ieps`, {
        days_ahead: 60,
        custom_message: notifyMessage || null
      }, { withCredentials: true });
      
      toast.success(`Notifications sent to ${response.data.sent_count} team member(s)`);
      setShowNotifyDialog(false);
      setNotifyMessage('');
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="text-text-muted hover:text-primary">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <SpEdKitLogo size="sm" />
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-primary text-white hover:bg-primary/90 rounded-full"
              data-testid="create-team-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Team
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div className="mb-8 animate-fade-in-up">
            <h2 className="font-heading font-bold text-lg text-text-primary mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#6366F1]" />
              Pending Invitations
            </h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {pendingInvitations.map((invite) => (
                <div 
                  key={invite.invite_id}
                  className="bg-[#6366F1]/5 border-2 border-[#6366F1]/20 rounded-xl p-4"
                  data-testid={`invitation-${invite.invite_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-heading font-semibold text-text-primary">{invite.team_name}</h3>
                      <p className="text-sm text-text-muted">Invited by {invite.invited_by}</p>
                    </div>
                    <Badge variant="outline" className="bg-[#6366F1]/10 text-[#6366F1] border-[#6366F1]/30">
                      Pending
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAcceptInvitation(invite.invite_id)}
                      size="sm"
                      className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-full"
                      data-testid={`accept-invite-${invite.invite_id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Accept
                    </Button>
                    <Button
                      onClick={() => handleDeclineInvitation(invite.invite_id)}
                      size="sm"
                      variant="outline"
                      className="flex-1 rounded-full"
                      data-testid={`decline-invite-${invite.invite_id}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Teams List */}
          <div className="lg:col-span-1">
            <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              My Teams
            </h2>
            
            {teams.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-dashed border-gray-200 p-8 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="font-heading font-semibold text-text-primary mb-1">No Teams Yet</h3>
                <p className="text-sm text-text-muted mb-4">Create a team to collaborate with colleagues</p>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Team
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {teams.map((team) => (
                  <div
                    key={team.team_id}
                    onClick={() => fetchTeamDetails(team.team_id)}
                    className={`bg-white rounded-xl p-4 border-2 cursor-pointer transition-all hover:shadow-float ${
                      selectedTeam?.team_id === team.team_id 
                        ? 'border-primary shadow-float' 
                        : 'border-gray-100 hover:border-primary/30'
                    }`}
                    data-testid={`team-card-${team.team_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          team.role === 'leader' ? 'bg-amber-100' : 'bg-primary/10'
                        }`}>
                          {team.role === 'leader' ? (
                            <Crown className="w-5 h-5 text-amber-600" />
                          ) : (
                            <Users className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-text-primary">{team.name}</h3>
                          <p className="text-xs text-text-muted">
                            {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={team.role === 'leader' 
                          ? 'bg-amber-50 text-amber-700 border-amber-200' 
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                        }
                      >
                        {team.role === 'leader' ? 'Leader' : 'Member'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Team Details */}
          <div className="lg:col-span-2">
            {selectedTeam ? (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                {/* Team Header */}
                <div className="bg-gradient-to-r from-primary/10 to-secondary/30 p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="font-heading font-bold text-2xl text-text-primary flex items-center gap-2">
                        {selectedTeam.name}
                        {selectedTeam.role === 'leader' && (
                          <Crown className="w-5 h-5 text-amber-500" />
                        )}
                      </h2>
                      {selectedTeam.description && (
                        <p className="text-text-secondary mt-1">{selectedTeam.description}</p>
                      )}
                      <p className="text-sm text-text-muted mt-2">
                        {selectedTeam.member_count} of {MAX_TEAM_MEMBERS} members
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {selectedTeam.role === 'leader' ? (
                          <>
                            <DropdownMenuItem 
                              onClick={() => setShowNotifyDialog(true)}
                              className="cursor-pointer"
                            >
                              <Bell className="w-4 h-4 mr-2" />
                              Notify Team of Due IEPs
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setShowInviteDialog(true)}
                              className="cursor-pointer"
                            >
                              <UserPlus className="w-4 h-4 mr-2" />
                              Invite Member
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setShowDeleteTeamDialog(true)}
                              className="cursor-pointer text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Team
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuItem 
                            onClick={() => setShowLeaveTeamDialog(true)}
                            className="cursor-pointer text-red-600"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            Leave Team
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Notify Team Action Banner (Leader Only) */}
                {selectedTeam.role === 'leader' && selectedTeam.members?.length > 0 && (
                  <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <Bell className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-heading font-semibold text-text-primary text-sm">IEP Due Date Reminders</p>
                          <p className="text-xs text-text-muted">Notify team members about upcoming IEPs</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setShowNotifyDialog(true)}
                        className="bg-amber-500 text-white hover:bg-amber-600 rounded-full"
                        size="sm"
                        data-testid="notify-team-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Notify Team
                      </Button>
                    </div>
                  </div>
                )}

                {/* Team Leader */}
                <div className="p-6 border-b">
                  <h3 className="font-heading font-semibold text-sm text-text-muted uppercase tracking-wider mb-3">
                    Team Leader
                  </h3>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Crown className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{selectedTeam.leader_name}</p>
                      <p className="text-sm text-text-muted">{selectedTeam.leader_email}</p>
                    </div>
                  </div>
                </div>

                {/* Team Members */}
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-semibold text-sm text-text-muted uppercase tracking-wider">
                      Members ({selectedTeam.members?.length || 0})
                    </h3>
                    {selectedTeam.role === 'leader' && selectedTeam.member_count < MAX_TEAM_MEMBERS && (
                      <Button
                        onClick={() => setShowInviteDialog(true)}
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        data-testid="invite-member-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Invite
                      </Button>
                    )}
                  </div>
                  
                  {selectedTeam.members?.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-text-muted">No members yet</p>
                      {selectedTeam.role === 'leader' && (
                        <Button
                          onClick={() => setShowInviteDialog(true)}
                          size="sm"
                          className="mt-3 bg-primary text-white hover:bg-primary/90 rounded-full"
                        >
                          Invite Your First Member
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedTeam.members.map((member) => (
                        <div 
                          key={member.user_id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-primary font-medium">
                                {member.name?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-text-primary">{member.name}</p>
                              <p className="text-sm text-text-muted">{member.email}</p>
                            </div>
                          </div>
                          {selectedTeam.role === 'leader' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-red-500 hover:bg-red-50"
                              onClick={() => setShowRemoveMemberDialog(member)}
                              data-testid={`remove-member-${member.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pending Invitations (Leader Only) */}
                {selectedTeam.role === 'leader' && selectedTeam.pending_invitations?.length > 0 && (
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-sm text-text-muted uppercase tracking-wider mb-3">
                      Pending Invitations ({selectedTeam.pending_invitations.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedTeam.pending_invitations.map((invite) => (
                        <div 
                          key={invite.invite_id}
                          className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                              <Mail className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-text-primary">{invite.email}</p>
                              <p className="text-xs text-text-muted">Invited {new Date(invite.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                            Pending
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">Select a Team</h3>
                <p className="text-text-muted">Click on a team to view details and manage members</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Team Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Create a Team
            </DialogTitle>
            <DialogDescription className="font-body">
              Teams allow you to collaborate with colleagues and share student folders.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="font-body font-semibold">Team Name *</Label>
              <Input
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                placeholder="e.g., Grade 3 Special Ed Team"
                className="mt-2"
                data-testid="team-name-input"
              />
            </div>
            
            <div>
              <Label className="font-body font-semibold">Description (Optional)</Label>
              <Textarea
                value={newTeam.description}
                onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                placeholder="Describe what this team is for..."
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTeam} 
              disabled={saving || !newTeam.name.trim()}
              className="bg-primary text-white rounded-full"
              data-testid="create-team-submit"
            >
              {saving ? 'Creating...' : 'Create Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#6366F1]" />
              Invite Team Member
            </DialogTitle>
            <DialogDescription className="font-body">
              Send an email invitation to join {selectedTeam?.name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="font-body font-semibold">Email Address *</Label>
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="colleague@school.edu"
              className="mt-2"
              data-testid="invite-email-input"
            />
            <p className="text-xs text-text-muted mt-2">
              They'll receive an email with instructions to join your team.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleInviteMember} 
              disabled={saving || !inviteEmail.trim()}
              className="bg-[#6366F1] text-white rounded-full"
              data-testid="send-invite-btn"
            >
              {saving ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Dialog */}
      <AlertDialog open={showDeleteTeamDialog} onOpenChange={setShowDeleteTeamDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTeam?.name}"? This will remove all members and shared folder access. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeam}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-team"
            >
              Delete Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Leave Team Dialog */}
      <AlertDialog open={showLeaveTeamDialog} onOpenChange={setShowLeaveTeamDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave Team?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave "{selectedTeam?.name}"? You'll lose access to any shared folders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLeaveTeam}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Leave Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Member Dialog */}
      <AlertDialog open={!!showRemoveMemberDialog} onOpenChange={() => setShowRemoveMemberDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {showRemoveMemberDialog?.name} from the team? They'll lose access to all shared folders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRemoveMember(showRemoveMemberDialog?.user_id)}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Notify Team Dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Notify Team About Due IEPs
            </DialogTitle>
            <DialogDescription className="font-body">
              Send an email notification to all {selectedTeam?.members?.length || 0} team member(s) about upcoming IEP due dates for shared students.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">What will be included:</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700">
                    <li>All upcoming IEP due dates (next 60 days)</li>
                    <li>Overdue IEPs (highlighted)</li>
                    <li>Student names and goal summaries</li>
                    <li>Recommended actions checklist</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="font-body font-semibold">Personal Message (Optional)</Label>
              <Textarea
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="Add a personal note to your team..."
                className="mt-2"
                rows={3}
                data-testid="notify-message-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowNotifyDialog(false);
                setNotifyMessage('');
              }} 
              className="rounded-full"
              disabled={notifying}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleNotifyTeam} 
              disabled={notifying}
              className="bg-amber-500 text-white hover:bg-amber-600 rounded-full"
              data-testid="send-notification-btn"
            >
              {notifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Notification
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Teams;
